import DOMPurify from "dompurify";

// HTML-маркеры из Avito-allowlist: если хоть один встретился — считаем входной
// шаблон HTML'ом и прогоняем через DOMPurify. Иначе обрабатываем как legacy
// plain-text.
export const HTML_TAG_REGEX = /<(?:p|br|strong|em|b|i|ul|ol|li)\b/i;

// Allowlist для path в `{{...}}`: алфанумерик + точка/подчёркивание/дефис +
// кириллица (params.Цвет, properties.Материал валидны). Запрещаем
// HTML-спецсимволы и пробелы внутри path — иначе round-trip через
// атрибут даст рассинхрон. Lazy `*` + negated class — ReDoS-safe.
const PLACEHOLDER_REGEX = /\{\{\s*([^\s{}<>&"']+)\s*\}\}/g;

const AVITO_ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "ul",
  "ol",
  "li",
] as const;

const escapeHtml = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const isTextNode = (n: globalThis.Node): n is Text => n.nodeType === 3;

// Парсит произвольный HTML в inert DocumentFragment через DOMParser — без
// присваивания innerHTML на live-элементе (DOMParser не выполняет script'ы
// и не загружает ресурсы по spec'у HTML Standard для документов без
// browsing context).
const parseToFragment = (html: string): DocumentFragment => {
  const doc = new DOMParser().parseFromString(
    `<body>${html}</body>`,
    "text/html",
  );
  const frag = doc.createDocumentFragment();
  while (doc.body.firstChild) frag.appendChild(doc.body.firstChild);
  return frag;
};

// Серилизует фрагмент обратно в HTML-строку. `outerHTML`-getter безопасен:
// он только читает текущее DOM-дерево, без интерпретации входной строки.
const serializeFragment = (frag: DocumentFragment): string => {
  const container = document.createElement("div");
  container.appendChild(frag);
  let out = "";
  for (const child of Array.from(container.childNodes)) {
    out += child instanceof Element ? child.outerHTML : child.textContent ?? "";
  }
  return out;
};

// Обходит только text-nodes и подменяет в них `{{path}}` на placeholder-span.
// DOM-walk вместо regex-replace на HTML-строке: `{{x}}` внутри атрибута или
// комментария не затронется, а span строится через setAttribute/textContent —
// автоматический эскейп даёт корректный round-trip для путей с любыми
// символами allowlist'а.
const wrapPlaceholdersInTextNodes = (
  frag: DocumentFragment,
): DocumentFragment => {
  const textNodes: Text[] = [];
  const walker = document.createTreeWalker(frag, NodeFilter.SHOW_TEXT);
  let current = walker.nextNode();
  while (current) {
    if (isTextNode(current)) textNodes.push(current);
    current = walker.nextNode();
  }
  for (const textNode of textNodes) {
    const text = textNode.textContent ?? "";
    const matches = [...text.matchAll(PLACEHOLDER_REGEX)];
    if (matches.length === 0) continue;
    const replacement = document.createDocumentFragment();
    let cursor = 0;
    for (const m of matches) {
      const start = m.index ?? 0;
      if (start > cursor) {
        replacement.appendChild(
          document.createTextNode(text.slice(cursor, start)),
        );
      }
      const path = m[1];
      const span = document.createElement("span");
      span.setAttribute("data-placeholder", path);
      span.textContent = `{{${path}}}`;
      replacement.appendChild(span);
      cursor = start + m[0].length;
    }
    if (cursor < text.length) {
      replacement.appendChild(document.createTextNode(text.slice(cursor)));
    }
    textNode.replaceWith(replacement);
  }
  return frag;
};

/**
 * template-строка из backend → HTML для TipTap. Принимает оба формата:
 *  - HTML с avito-тегами: прогоняем через DOMPurify до allowlist
 *    (defense-in-depth к ProseMirror schema, которая иначе единственный
 *    фильтр).
 *  - Plain text + `{{vars}}` (legacy): эскейпим спецсимволы, `\n\n` → `</p><p>`,
 *    одиночный `\n` → `<br>`.
 *
 * После — обходим text-nodes и оборачиваем `{{path}}` в маркер-span,
 * который `PlaceholderNode.parseHTML` подхватит и превратит в atom-ноду.
 */
export const templateToHtml = (template: string): string => {
  if (!template) return "";
  const baseHtml = HTML_TAG_REGEX.test(template)
    ? DOMPurify.sanitize(template, {
        ALLOWED_TAGS: [...AVITO_ALLOWED_TAGS],
        ALLOWED_ATTR: [],
      })
    : escapeHtml(template)
        .split(/\n{2,}/)
        .map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`)
        .join("");
  return serializeFragment(
    wrapPlaceholdersInTextNodes(parseToFragment(baseHtml)),
  );
};

/**
 * `editor.getHTML()` → template-строка для backend. Снимает обёртку
 * placeholder-span, оставляя литеральный `{{path}}`. DOM-обход даёт
 * корректное декодирование любых HTML-entities в `data-placeholder` без
 * самописного частичного декодера.
 */
export const htmlToTemplate = (html: string): string => {
  if (!html) return "";
  const frag = parseToFragment(html);
  for (const span of Array.from(
    frag.querySelectorAll("span[data-placeholder]"),
  )) {
    const path = span.getAttribute("data-placeholder") ?? "";
    span.replaceWith(`{{${path}}}`);
  }
  return serializeFragment(frag);
};

/**
 * Аварийная конверсия HTML-шаблона обратно в plain-text. Нужна при смене
 * формата экспорта с Avito на не-Avito: rich-описание уже лежит в форме как
 * HTML, а textarea-чип-редактор и форматтеры без санитизации запишут литералы
 * `<p>`/`<br>` в фид. Поэтому один раз на переходе вырезаем теги до
 * plain-text с сохранением переносов и `{{path}}`-литералов.
 */
export const htmlTemplateToPlain = (html: string): string => {
  if (!html || !HTML_TAG_REGEX.test(html)) return html;
  const frag = parseToFragment(html);
  for (const span of Array.from(
    frag.querySelectorAll("span[data-placeholder]"),
  )) {
    const path = span.getAttribute("data-placeholder") ?? "";
    span.replaceWith(`{{${path}}}`);
  }
  for (const br of Array.from(frag.querySelectorAll("br"))) {
    br.replaceWith("\n");
  }
  const blocks: string[] = [];
  for (const child of Array.from(frag.children)) {
    if (child.tagName === "UL" || child.tagName === "OL") {
      const items = Array.from(child.querySelectorAll("li")).map(
        (li) => `- ${li.textContent ?? ""}`,
      );
      blocks.push(items.join("\n"));
    } else {
      blocks.push(child.textContent ?? "");
    }
  }
  return blocks.join("\n\n").trim();
};
